<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Toast } from "bootstrap";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import Bs5CheatSheet from "./components/BS5CheatSheet.vue";
import SampleDesignShowcase from "./SampleDesignShowcase.vue";
import ThemeSwitcher from "./components/ThemeSwitcher.vue";

const route = useRoute();
const router = useRouter();

const tabs = ['elements', 'components', 'theme'] as const;

const activeTab = computed(() => tabs.find(t => route.name === `styleguide-${t}`) ?? 'elements');

const breadcrumbs = computed(() => [
  { label: 'Dashboard', href: '/' },
  { label: 'Style Guide', href: '/styleguide' },
  { label: activeTab.value.charAt(0).toUpperCase() + activeTab.value.slice(1) },
]);

function isTab(tab: 'elements' | 'components' | 'theme') {
  return route.name === `styleguide-${tab}`;
}

onMounted(() => {
  const toastTrigger = document.getElementById("liveToastBtn");
  const toastLiveExample = document.getElementById("liveToast");
  if (toastTrigger) {
    toastTrigger.addEventListener("click", () => {
      const toast = new Toast(toastLiveExample as HTMLElement);
      toast.show();
    });
  }
});
</script>

<template>
  <main>
    <PanelGrid :columns="1">
      <PanelCard>
        <PageHeader title="Style Guide" :breadcrumbs="breadcrumbs" />

        <!-- Top-level tab navigation -->
        <ul class="nav nav-tabs mb-4">
          <li class="nav-item">
            <button
              class="nav-link"
              :class="{ active: isTab('elements') }"
              @click="router.push({ name: 'styleguide-elements' })"
            >
              <i class="bi bi-brush me-1"></i>Elements
            </button>
          </li>
          <li class="nav-item">
            <button
              class="nav-link"
              :class="{ active: isTab('components') }"
              @click="router.push({ name: 'styleguide-components' })"
            >
              <i class="bi bi-easel me-1"></i>Components
            </button>
          </li>
          <li class="nav-item">
            <button
              class="nav-link"
              :class="{ active: isTab('theme') }"
              @click="router.push({ name: 'styleguide-theme' })"
            >
              <i class="bi bi-palette me-1"></i>Theme
            </button>
          </li>
        </ul>

        <!-- =====================================================================
             ELEMENTS TAB CONTENT
             ===================================================================== -->
        <div v-if="isTab('elements')">

          <!-- Typography Section -->
          <h2 class="cards-header">Typography</h2>
          <hr />
          <div class="row mb-4">
            <div class="col-md-6">
              <h1>Heading 1</h1>
              <h2>Heading 2</h2>
              <h3>Heading 3 - cards-header</h3>
              <h4>Heading 4</h4>
              <h5>Heading 5</h5>
              <h6>Heading 6</h6>
            </div>
            <div class="col-md-6">
              <p class="fs-1">Display text fs-1</p>
              <p class="fs-2">Display text fs-2</p>
              <p class="fs-3">Display text fs-3</p>
              <p class="lead">Lead paragraph text</p>
              <p>Regular paragraph text</p>
              <p class="small">Small text</p>
              <p class="text-muted">Muted text</p>
            </div>
          </div>
          <h3>Font Weights & Styles</h3>
          <p class="fw-bold">Bold text</p>
          <p class="fw-medium">Medium weight text</p>
          <p class="fw-normal">Normal weight text</p>
          <p class="fw-light">Light weight text</p>
          <p class="fst-italic">Italic text</p>
          <p class="text-decoration-underline">Underlined text</p>
          <p class="text-uppercase">Uppercase text</p>

          <!-- Color Palette Section -->
          <h2 class="cards-header mt-4">Color Palette</h2>
          <hr />
          <h3>Brand Colors</h3>
          <div class="row g-3 mb-4">
            <div class="col-md-3">
              <div class="bg-primary p-4 rounded mb-2 text-white text-center">
                <strong>Primary</strong><br /><small>#33658a</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="bg-secondary p-4 rounded mb-2 text-center">
                <strong>Secondary</strong><br /><small>#f93</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="bg-info p-4 rounded mb-2 text-center">
                <strong>Info</strong><br /><small>#5b9bd1</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="bg-light p-4 rounded mb-2 border text-center">
                <strong>Light</strong><br /><small>#f3f5f9</small>
              </div>
            </div>
          </div>
          <h3>Status Colors</h3>
          <div class="row g-3 mb-4">
            <div class="col-md-3">
              <div class="bg-success p-4 rounded mb-2 text-center">
                <strong>Success</strong><br /><small>#166534</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="bg-warning p-4 rounded mb-2 text-center">
                <strong>Warning</strong><br /><small>#fd9d17</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="bg-danger p-4 rounded mb-2 text-center">
                <strong>Danger</strong><br /><small>#dc2626</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="bg-dark p-4 rounded mb-2 text-center">
                <strong>Dark</strong><br /><small>#212529</small>
              </div>
            </div>
          </div>
          <h3>Custom App Colors</h3>
          <div class="row g-3 mb-4">
            <div class="col-md-3">
              <div class="bg-blackbox p-4 rounded mb-2 text-center">
                <strong>Blackbox</strong><br /><small>bg-blackbox</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="bg-error p-4 rounded mb-2 text-center">
                <strong>Error</strong><br /><small>bg-error</small>
              </div>
            </div>
          </div>

          <!-- Icons & Status Indicators Section -->
          <h2 class="cards-header mt-4">Icons & Status Indicators</h2>
          <hr />
          <h3>Status Icons</h3>
          <div class="d-flex align-items-center gap-4 mb-4">
            <div class="text-center">
              <i class="bi bi-check fs-3 text-success"></i><br /><small>Success</small>
            </div>
            <div class="text-center">
              <i class="bi bi-x fs-3 text-danger"></i><br /><small>Error</small>
            </div>
            <div class="text-center">
              <i class="bi bi-exclamation-triangle fs-3 text-warning"></i><br /><small>Warning</small>
            </div>
            <div class="text-center">
              <i class="bi bi-clock fs-3 text-info"></i><br /><small>Pending</small>
            </div>
            <div class="text-center">
              <i class="bi bi-info-circle fs-3 text-primary"></i><br /><small>Info</small>
            </div>
          </div>

          <h3>Run Status (icon + badge)</h3>
          <p class="text-muted small mb-2">
            Run-history status. The icon uses the shared <code>.run-status-*</code> palette
            (<code>styles.scss</code>) so it reads as the same hue as its status badge — not
            Bootstrap's vivid <code>text-*</code> tokens. Used by ServiceNode / StackedServiceCard.
          </p>
          <div class="d-flex align-items-center gap-4 mb-4 flex-wrap">
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-check-circle fs-4 run-status-succeeded"></i>
              <span class="badge bg-success">Succeeded</span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-x-circle fs-4 run-status-failed"></i>
              <span class="badge bg-danger">Failed</span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-exclamation-triangle fs-4 run-status-warning"></i>
              <span class="badge bg-warning">Warning</span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-arrow-repeat fs-4 run-status-running"></i>
              <span class="badge bg-primary">Running</span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-clock fs-4 run-status-pending"></i>
              <span class="badge bg-secondary">Pending</span>
            </div>
          </div>

          <h3>Badges</h3>
          <div class="d-flex gap-2 flex-wrap mb-3">
            <span class="badge bg-primary">Primary</span>
            <span class="badge bg-secondary">Secondary</span>
            <span class="badge bg-success">Success</span>
            <span class="badge bg-danger">Danger</span>
            <span class="badge bg-warning">Warning</span>
            <span class="badge bg-info">Info</span>
            <span class="badge bg-light text-dark">Light</span>
            <span class="badge bg-dark">Dark</span>
          </div>
          <h4>Pastel Badges (App Style)</h4>
          <div class="d-flex gap-2 flex-wrap mb-4">
            <span class="badge bg-success">Success Pastel</span>
            <span class="badge bg-danger">Danger Pastel</span>
            <span class="badge bg-warning">Warning Pastel</span>
            <span class="badge bg-primary">Primary Pastel</span>
            <span class="badge bg-info">Info Pastel</span>
            <span class="badge bg-secondary">Secondary Pastel</span>
          </div>

          <!-- Buttons Section -->
          <h2 class="cards-header mt-4">Buttons</h2>
          <hr />
          <h3>Standard Buttons</h3>
          <div class="mb-3">
            <button class="btn btn-primary me-1">Primary</button>
            <button class="btn btn-secondary me-1">Secondary</button>
            <button class="btn btn-info me-1">Info</button>
            <button class="btn btn-success me-1">Success</button>
            <button class="btn btn-danger me-1">Danger</button>
            <button class="btn btn-dark me-1">Dark</button>
            <button class="btn btn-light me-1">Light</button>
          </div>
          <h3>Outline Buttons</h3>
          <div class="mb-3">
            <button class="btn btn-outline-primary me-1">Primary</button>
            <button class="btn btn-outline-secondary me-1">Secondary</button>
            <button class="btn btn-outline-success me-1">Success</button>
            <button class="btn btn-outline-danger me-1">Danger</button>
          </div>
          <h3>Custom App Buttons</h3>
          <div class="mb-3">
            <button class="btn text-light bg-pending me-1">Pending</button>
            <button class="btn text-light bg-success me-1">Success</button>
            <button class="btn text-light bg-error me-1">Error</button>
            <button class="btn text-dark bg-warning me-1">Warning</button>
          </div>
          <h3>Button Sizes</h3>
          <div class="mb-4">
            <button class="btn btn-primary btn-lg me-1">Large</button>
            <button class="btn btn-primary me-1">Regular</button>
            <button class="btn btn-primary btn-sm me-1">Small</button>
          </div>

          <!-- Common Components Section -->
          <h2 class="cards-header mt-4">Common Components</h2>
          <hr />
          <h3>Cards</h3>
          <div class="row g-3 mb-4">
            <div class="col-md-4">
              <div class="card">
                <div class="card-body">
                  <h6 class="card-title">Basic Card</h6>
                  <p class="card-text">Standard card with minimal styling.</p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card card-hover">
                <div class="card-body">
                  <h6 class="card-title">Hover Card</h6>
                  <p class="card-text">Card with hover effects.</p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="stat-card">
                <div class="stat-value">42</div>
                <div class="stat-label">Stat Card</div>
              </div>
            </div>
          </div>
          <h3>Special Badges</h3>
          <div class="mb-4">
            <span class="publisher-master-badge me-2">Publisher Master</span>
            <span class="badge bg-light text-dark border me-2">Filtered Badge</span>
          </div>
          <h3>Progress & Loading</h3>
          <div class="mb-3">
            <div class="progress mb-2">
              <div class="progress-bar bg-success" style="width: 75%"></div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
              <span>Loading...</span>
            </div>
          </div>
          <h3>Text Colors</h3>
          <div class="mb-4">
            <p class="text-primary">Primary text color</p>
            <p class="text-secondary">Secondary text color</p>
            <p class="text-success">Success text color</p>
            <p class="text-danger">Danger text color</p>
            <p class="text-warning">Warning text color</p>
            <p class="text-info">Info text color</p>
            <p class="text-muted">Muted text color</p>
            <p class="text-dark">Dark text color</p>
          </div>

          <!-- BS5 Cheat Sheet -->
          <h2 class="cards-header mt-4">Bootstrap 5 Cheat Sheet</h2>
          <hr />
          <bs5-cheat-sheet></bs5-cheat-sheet>
        </div>

        <!-- =====================================================================
             COMPONENTS TAB CONTENT
             ===================================================================== -->
        <div v-if="isTab('components')">
          <SampleDesignShowcase />
        </div>

        <!-- =====================================================================
             THEME TAB CONTENT
             ===================================================================== -->
        <div v-if="isTab('theme')">
          <ThemeSwitcher />
        </div>

      </PanelCard>
    </PanelGrid>
  </main>
</template>

<style></style>
